import type {
  AiControlledCommandDefinition,
  CommandExecutionState,
  CommandRejection,
} from './types';
import type { AiControlledCommandId } from './aiControlledCommandId';

export type CommandValidationResult<Payload> =
  | { status: 'valid'; payload: Payload }
  | CommandRejection;

type CommandMetadata = {
  name: AiControlledCommandId;
  domain: string;
  description: string;
  parameters: object;
  contextDescription: string;
  requiresAppShell: boolean;
};

type AiControlledCommandSpec<
  Payload,
  Context extends object | null,
> = CommandMetadata & {
  isContext: (context: unknown) => context is Context;
  validate: (
    rawPayload: unknown,
    context: Context,
    state: CommandExecutionState
  ) => CommandValidationResult<Payload>;
  /** Returns a rejection when the command turns out to be inapplicable while executing. */
  execute: (
    payload: Payload,
    state: CommandExecutionState
  ) => void | CommandRejection | Promise<void | CommandRejection>;
  isApplied: (payload: Payload, context: Context) => boolean;
  /** What the command did, reported to the assistant once it has applied. */
  describeApplied?: (payload: Payload) => unknown;
};

export const defineAiControlledCommand = <
  Payload,
  Context extends object | null,
>({
  isContext,
  validate,
  execute,
  isApplied,
  describeApplied,
  ...metadata
}: AiControlledCommandSpec<
  Payload,
  Context
>): AiControlledCommandDefinition<Context> => ({
  ...metadata,
  resolve: (rawPayload, advertisedContext, state) => {
    if (!isContext(advertisedContext)) {
      return {
        status: 'rejected',
        reason: 'The requested command is no longer available.',
      };
    }

    const validation = validate(rawPayload, advertisedContext, state);
    if (validation.status === 'rejected') return validation;

    const { payload } = validation;
    return {
      status: 'ready',
      execute: async () =>
        (await execute(payload, state)) ?? { status: 'executed' },
      isApplied: context => isContext(context) && isApplied(payload, context),
      describeApplied: () => describeApplied?.(payload),
    };
  },
});
